/**
 * Labstep
 *
 * @module components/ProtocolValue/Form/ShowEdit/ResourceItem/Input/Bulk
 * @desc ProtocolValue ShowEdit ResourceItem Input Bulk for variable
 */

import {
  useHasAccess,
  useHasAccessCreate,
} from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { ICONS } from 'labstep-web/constants/icons';
import { SearchSelectModalContainer as SearchSelectModal } from 'labstep-web/core/Select/Search/Modal';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type React from 'react';
import { getSearchSelectProps } from '../../../Form/ShowEdit/ResourceItem/Input/utils';
import type { IProtocolValueVariableActionSetItemsProps } from './types';
import { getOnChange } from './utils';

export const ProtocolValueVariableActionSetItems: React.FC<
  IProtocolValueVariableActionSetItemsProps
> = ({
  protocolValueTemplate,
  createProtocolCondition,
  createProtocolValue,
  updateProtocolValue,
  protocolValuesOrConditions = [],
  ...rest
}) => {
  const canSelectItems = useHasAccess(
    protocolValueTemplate.entityName,
    protocolValueTemplate.guid,
    Action.edit,
    ['resource', 'resource_item'],
  );

  const canCreateProtocolConditions = useHasAccessCreate(
    ProtocolCondition.entityName,
    protocolValueTemplate.entityName,
    protocolValueTemplate.idAttr,
  );

  const canCreateProtocolValues = useHasAccessCreate(
    ProtocolValue.entityName,
    protocolValueTemplate.entityName,
    protocolValueTemplate.idAttr,
  );

  const protocolConditions = protocolValuesOrConditions.filter(
    (protocolValueOrCondition) =>
      protocolValueOrCondition instanceof ProtocolCondition,
  ) as ProtocolCondition[];

  const editItems =
    protocolValuesOrConditions.length > 1 &&
    protocolValueTemplate.is_input;

  const canEdit = editItems
    ? canSelectItems &&
      (protocolConditions.length > 0 ? canCreateProtocolValues : true)
    : canCreateProtocolConditions;

  return (
    <SearchSelectModal
      {...getSearchSelectProps(protocolValueTemplate)}
      entityName={ResourceItem.entityName}
      canEdit={canEdit}
      rowSelection="multiple"
      requiredCount={
        editItems ? protocolValuesOrConditions.length : undefined
      }
      onChange={getOnChange(
        protocolValueTemplate,
        createProtocolCondition,
        createProtocolValue,
        updateProtocolValue,
        protocolValuesOrConditions,
      )}
      actionComponentProps={{
        type: 'text',
        text: editItems
          ? `Select ${ResourceItem.getHumanReadableEntityName(
              true,
              true,
            )}`
          : `Add ${ProtocolCondition.getHumanReadableEntityName(
              true,
              true,
            )}`,
        icon: editItems ? ICONS.resource_item.primary : 'plus',
      }}
      {...rest}
    />
  );
};
